// Globals
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Form, FormItem, PhoneInput, useForm, useWatchForm } from '@/components';
import { Button, Checkbox, Input, Select } from '@/ui';

// Modules
import { SupportedLocales } from '@/models/localization.model';

// Modules
import { LocalesFormFields } from '@/modules/Locales';
import { MapsProvider, MultiLangPlaceAutocomplete } from '@/modules/Maps';

// Helpers
import { getRegionActiveOptions } from '../../helpers/getOptions';
import { PickingFormFields, TCreatePickingFormValues } from '../../helpers/types';
import { useAutocompleteRegions, useAutocompleteSettlements, usePickupFormValidation } from '../../hooks';

type TPickupPointFormProps = {
  onSubmit?: (values: TCreatePickingFormValues) => void;
  isLoading?: boolean;
  initialValues?: Partial<TCreatePickingFormValues>;
  defaultRegion?: string;
  defaultSettlement?: string;
};

const initialLocale = {
  [SupportedLocales.UK]: { [PickingFormFields.WORKING_HOURS]: '' },
  [SupportedLocales.RU]: { [PickingFormFields.WORKING_HOURS]: '' },
};

const PickupPointForm = ({
  onSubmit,
  isLoading,
  initialValues,
  defaultRegion,
  defaultSettlement,
}: TPickupPointFormProps) => {
  const { t } = useTranslation(['regions']);
  const [form] = useForm();
  const rules = usePickupFormValidation();
  const { onSearch: onSearchRegions, regionsOptions } = useAutocompleteRegions(defaultRegion);
  const { onSearch: onSearchSettlements, settlementsOptions } = useAutocompleteSettlements(defaultSettlement);
  const selectedRegionId = useWatchForm<string>(PickingFormFields.REGION, form);
  const selectedSettlementId = useWatchForm<string>(PickingFormFields.SETTLEMENT, form);

  const selectedSettlementCityCenter = useMemo(() => {
    const settlement = settlementsOptions?.find((item) => item.value === selectedSettlementId);
    return (
      settlement && {
        latitude: settlement?.latitude,
        longitude: settlement?.longitude,
      }
    );
  }, [settlementsOptions, selectedSettlementId]);

  const handleSearchSettlements = (value: string) => onSearchSettlements(value, selectedRegionId);

  return (
    <Form
      form={form}
      onFinish={onSubmit}
      initialValues={{
        [PickingFormFields.WORKING_HOURS]: initialLocale,
        ...initialValues,
      }}
    >
      <FormItem label={t('pickup_region')} name={PickingFormFields.REGION} rules={rules[PickingFormFields.REGION]}>
        <Select
          filterOption={false}
          options={regionsOptions ?? []}
          size="middle"
          onSearch={onSearchRegions}
          allowClear
          showSearch
          placeholder={t('search_region_placeholder')}
        />
      </FormItem>
      <FormItem
        label={t('pickup_city')}
        name={PickingFormFields.SETTLEMENT}
        rules={rules[PickingFormFields.SETTLEMENT]}
      >
        <Select
          filterOption={false}
          options={settlementsOptions ?? []}
          size="middle"
          onSearch={handleSearchSettlements}
          allowClear
          showSearch
          placeholder={t('search_city_placeholder')}
        />
      </FormItem>
      <FormItem label={t('pickup_phone_number')} name={PickingFormFields.PHONE} rules={rules[PickingFormFields.PHONE]}>
        <PhoneInput />
      </FormItem>
      <MapsProvider>
        <FormItem label={t('pickup_address')} name={PickingFormFields.ADDRESS} rules={rules[PickingFormFields.ADDRESS]}>
          <MultiLangPlaceAutocomplete cityCenter={selectedSettlementCityCenter} />
        </FormItem>
      </MapsProvider>
      <FormItem name={PickingFormFields.WORKING_HOURS}>
        <LocalesFormFields
          labelPrefix={'pickup_'}
          ns="regions"
          name={PickingFormFields.WORKING_HOURS}
          rules={rules[PickingFormFields.WORKING_HOURS]}
        />
      </FormItem>
      <FormItem
        label={t('pickup_status')}
        name={PickingFormFields.IS_ACTIVE}
        rules={rules[PickingFormFields.IS_ACTIVE]}
      >
        <Select options={getRegionActiveOptions(t)} size="middle" />
      </FormItem>
      <FormItem
        label={t('pickup_google_store_code')}
        name={PickingFormFields.GOOGLE_STORE_CODE}
        rules={[{ max: 64 }]}
        extra={t('pickup_google_store_code_hint')}
      >
        <Input size="middle" allowClear />
      </FormItem>
      <FormItem name={PickingFormFields.IS_DEFAULT} valuePropName="checked">
        <Checkbox placeholder={t('pickup_main')} />
      </FormItem>

      <Button htmlType="submit" block loading={isLoading} type="primary">
        {t('button_common_save', {
          ns: 'common',
        })}
      </Button>
    </Form>
  );
};

export default PickupPointForm;
